/**
 * LoopNest Media Provider — Base Interface
 * All providers must implement this interface.
 * Providers are easily replaceable when LoopNest migrates platforms.
 */

export const PROVIDER_TYPES = {
  IMAGE: 'image',
  VIDEO: 'video',
  AUDIO: 'audio',
};

export const LICENSE_TYPES = {
  CC0: 'cc0',
  UNSPLASH: 'unsplash-license',
  PEXELS: 'pexels-license',
  PIXABAY: 'pixabay-cc0',
  USER_OWNED: 'user-owned',
  AI_GENERATED: 'ai-generated',
};

/**
 * Base provider class — extend this for each provider
 */
export class BaseMediaProvider {
  constructor(config = {}) {
    this.id = config?.id || 'unknown';
    this.label = config?.label || 'Unknown Provider';
    this.license = config?.license || LICENSE_TYPES?.CC0;
    this.requiresAttribution = config?.requiresAttribution || false;
    this.apiKey = config?.apiKey || null;
    this.baseUrl = config?.baseUrl || '';
    this.enabled = config?.enabled !== false;
  }

  /**
   * Search for media by query
   * @param {string} query - Search query
   * @param {object} options - { width, height, count, orientation }
   * @returns {Promise<MediaItem[]>}
   */
  async search(query, options = {}) {
    throw new Error(`Provider ${this.id} must implement search()`);
  }

  /**
   * Get a single media item by ID
   * @param {string} id
   * @returns {Promise<MediaItem>}
   */
  async getById(id) {
    throw new Error(`Provider ${this.id} must implement getById()`);
  }

  /**
   * Build attribution string if required
   * @param {MediaItem} item
   * @returns {string|null}
   */
  buildAttribution(item) {
    if (!this.requiresAttribution) return null;
    return `Photo by ${item?.author || 'Unknown'} on ${this.label}`;
  }

  /**
   * Validate that this provider is safe to use
   */
  isApproved() {
    return this.enabled && !!this.id;
  }
}

/**
 * @typedef {Object} MediaItem
 * @property {string} id - Unique ID
 * @property {string} url - Full-resolution URL
 * @property {string} previewUrl - Thumbnail/preview URL
 * @property {string} alt - Alt text description
 * @property {string} author - Creator name
 * @property {string} authorUrl - Creator profile URL
 * @property {string} providerId - Which provider this came from
 * @property {string} license - License type
 * @property {boolean} requiresAttribution - Whether attribution is needed
 * @property {number} width - Original width
 * @property {number} height - Original height
 */

export function createMediaItem(overrides = {}) {
  return {
    id: overrides?.id || `media-${Date.now()}`,
    url: overrides?.url || '',
    previewUrl: overrides?.previewUrl || overrides?.url || '',
    alt: overrides?.alt || '',
    author: overrides?.author || '',
    authorUrl: overrides?.authorUrl || '',
    providerId: overrides?.providerId || 'unknown',
    license: overrides?.license || LICENSE_TYPES?.CC0,
    requiresAttribution: overrides?.requiresAttribution || false,
    width: overrides?.width || 1280,
    height: overrides?.height || 720,
  };
}
